import { createReducer, on } from '@ngrx/store';
import { CCDataPerLocation, CCListState, initialState } from './cc-list-state';
import { ccDataAdapter, ccLocationsAdapter, ccRowSettingsAdapter } from './cc-list-adapters';
import { CCRowSetting } from '../../../../models/locations-models/cc-row-setting';
import { CCActionBarActions, CCListRefreshActions, CCOperationActions, CCRowActions,
  markAllCCListsAsStale, resetCCData, updateCCListSorting } from './cc-list-actions';
import { LocationsRefreshActions } from '../../location-list/state/location-list-actions';

export const ccListReducer = createReducer(initialState,
    on(CCListRefreshActions.updateCCListSuccess, (state, { focusedLocationId, ccLocations }): CCListState => {
      const ccRowSettings: CCRowSetting[] = ccLocations.map(ccLocation => {
        // if there is already a row setting for the iterated cc location, create a copy
        // this makes sure that selection and cc status is preserved
        if (state.ccData.entities[focusedLocationId]?.ccRowSettings?.[ccLocation.placeId]) {
          const rowSettingCopy = { ...state.ccData.entities[focusedLocationId].ccRowSettings[ccLocation.placeId] };
          // make sure that even if a row was not selected but the respective cc location is starred,
          // it will be selected now
          rowSettingCopy.selected = rowSettingCopy.selected || !!ccLocation.starred;
          return rowSettingCopy;
        }
        // create a row setting for the iterated cc location
        return {
          placeId: ccLocation.placeId,
          // if the iterated cc location is starred, select the respective row
          selected: !!ccLocation.starred,
          hovered: false,
          ongoingCcOperation: null,
        };
      });

      const ccDataPerLocation: CCDataPerLocation = {
        id: focusedLocationId,
        ccLocationsLoading: false,
        possiblyOutOfDate: false, // cannot be out-of-date after a successful refresh
        ccLocations: ccLocationsAdapter.setAll(ccLocations, state.ccData.entities[focusedLocationId]?.ccLocations ?
            state.ccData.entities[focusedLocationId]?.ccLocations :
            ccLocationsAdapter.getInitialState()),
        ccRowSettings: ccRowSettingsAdapter.setAll(ccRowSettings, state.ccData.entities[focusedLocationId]?.ccRowSettings ?
            state.ccData.entities[focusedLocationId]?.ccRowSettings :
            ccRowSettingsAdapter.getInitialState()),
        timestamp: Date.now(),
      }

      return {
        ...state,
        ccData: ccDataAdapter.upsertOne(ccDataPerLocation, state.ccData),
      };
    }),
    on(CCListRefreshActions.updateCCListError, (state, { focusedLocationId }): CCListState => {
      return {
        ...state,
        ccData: ccDataAdapter.removeOne(focusedLocationId, state.ccData),
      };
    }),
    on(CCListRefreshActions.updateCCLocationsLoading, (state, { focusedLocationId, loading }): CCListState => {
      const ccDataPerLocation: CCDataPerLocation = {
        ...state.ccData[focusedLocationId],
        ccLocationsLoading: loading,
        id: focusedLocationId,
      };

      return {
        ...state,
        ccData: ccDataAdapter.upsertOne(ccDataPerLocation, state.ccData),
      };
    }),
    on(CCListRefreshActions.patchLocation, (state, { focusedLocationId, ccLocation }): CCListState => {
      const rowSettingCopy = { ...state.ccData.entities[focusedLocationId].ccRowSettings[ccLocation.placeId] };
      // make sure that even if a row was not selected but the respective cc location is starred,
      // it will be selected now
      rowSettingCopy.selected = rowSettingCopy.selected || !!ccLocation.starred;

      const ccDataPerLocation: CCDataPerLocation = {
        ...state.ccData[focusedLocationId],
        id: focusedLocationId,
        ccRowSettings: ccRowSettingsAdapter.upsertOne(rowSettingCopy, state.ccData.entities[focusedLocationId]?.ccRowSettings),
        ccLocationsLoading: false,
        possiblyOutOfDate: false,
        timestamp: Date.now(),
        ccLocations: ccLocationsAdapter.upsertOne(ccLocation, state.ccData.entities[focusedLocationId]?.ccLocations)
      };

      return {
        ...state,
        ccData: ccDataAdapter.upsertOne(ccDataPerLocation, state.ccData),
      }
    }),


    on(CCActionBarActions.updateCCLocationGroup, (state, { ccLocationGroup }): CCListState => ({
      ...state, ccLocationGroup,
    })),
    on(CCActionBarActions.updateCCKeywordsSuccess, (state, { ccKeywords }): CCListState => ({
      ...state, ccKeywords,
    })),
    on(CCActionBarActions.updateCCRadiusSuccess, (state, { ccSearchRadius }): CCListState => ({
      ...state, ccRadius: ccSearchRadius,
    })),
    on(CCRowActions.setCcRowSettings, (state, { focusedLocationId, ccRowSettings }): CCListState => {
      const ccDataPerLocation: CCDataPerLocation = {
        ...state.ccData.entities[focusedLocationId],
        ccRowSettings: ccRowSettingsAdapter.setAll(ccRowSettings, state.ccData.entities[focusedLocationId]?.ccRowSettings),
      };

      return {
        ...state,
        ccData: ccDataAdapter.upsertOne(ccDataPerLocation, state.ccData),
      };
    }),
    on(CCRowActions.updateCcRowSetting,
        CCOperationActions.ccOperation,
        CCOperationActions.ccOperationError,
        CCOperationActions.ccOperationSuccess,
        (state, { focusedLocationId, ccRowSetting }): CCListState => {
          const ccDataPerLocation: CCDataPerLocation = {
            ...state.ccData.entities[focusedLocationId],
            ccRowSettings: ccRowSettingsAdapter.upsertOne(ccRowSetting, state.ccData.entities[focusedLocationId]?.ccRowSettings),
          };

          return { ...state, ccData: ccDataAdapter.upsertOne(ccDataPerLocation, state.ccData) };
        }),
    on(CCRowActions.updateManyCcRowSettings,
        (state, { focusedLocationId, ccRowSettings }): CCListState => {
          const ccDataPerLocation: CCDataPerLocation = {
            ...state.ccData.entities[focusedLocationId],
            ccRowSettings: ccRowSettingsAdapter.upsertMany(ccRowSettings, state.ccData.entities[focusedLocationId]?.ccRowSettings),
          };

          return { ...state, ccData: ccDataAdapter.upsertOne(ccDataPerLocation, state.ccData) };
        }),
    on(updateCCListSorting, (state, { sorting }): CCListState => ({
      ...state, sorting,
    })),
    on(CCActionBarActions.updateContinueAfterClaim, (state, { continueAfterClaim }): CCListState => ({
      ...state, continueAfterClaim,
    })),
    on(CCActionBarActions.updateContinueAfterCreate, (state, { continueAfterCreate }): CCListState => ({
      ...state, continueAfterCreate,
    })),
    // delete all cc locations from store
    on(resetCCData, (state): CCListState => ({
      ...state, ccData: ccDataAdapter.getInitialState(),
    })),
    // only keep cc locations of visible LSC locations
    on(LocationsRefreshActions.getEntireLocationListSuccess, (state, { locations }): CCListState => {
      const ccLocationsToBeRemoved = (state.ccData.ids as number[]).filter((id) =>
          !locations?.find(l => id == l.id));
      return { ...state, ccData: ccDataAdapter.removeMany(ccLocationsToBeRemoved, state.ccData) };
    }),
    // mark all cc locations as stale
    on(LocationsRefreshActions.patchLocation,
        LocationsRefreshActions.patchLocations,
        markAllCCListsAsStale,
        (state): CCListState => {
          const cachedCCData: CCDataPerLocation[] = state.ccData.ids.map(id => {
            return { ...state.ccData.entities[id], possiblyOutOfDate: true };
      });
          return { ...state, ccData: ccDataAdapter.setAll(cachedCCData, state.ccData) };
    }),
);
